import React, { JSX, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchPurchaseOrderDetail,
  clearCurrentPo,
  approvePurchaseOrder,
  rejectPurchaseOrder,
  submitPurchaseOrder,
  unsubmitPurchaseOrder,
  cancelPurchaseOrder,
  deletePurchaseOrder,
} from './poSlice';
import { AppDispatch, RootState } from '../../app/store';
import toast from 'react-hot-toast';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatUtils';

const STORE_PREVIEW_LIMIT = 5;

const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { current, detailLoading, actionLoading, error } = useSelector(
    (state: RootState) => state.purchaseOrders
  );
  const { user } = useSelector((state: RootState) => state.auth);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isQuotationOpen, setIsQuotationOpen] = useState(false);
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchPurchaseOrderDetail(Number(id)));
    return () => {
      dispatch(clearCurrentPo());
    };
  }, [dispatch, id]);

  const handleApprove = async () => {
    if (!current || !current.can_approve) return;
    try {
      await dispatch(approvePurchaseOrder(current.id)).unwrap();
      toast.success('Purchase order approved successfully');
    } catch (err: any) {
      toast.error(err || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!current || !current.can_reject || !rejectReason.trim()) return;
    try {
      await dispatch(rejectPurchaseOrder({ id: current.id, reason: rejectReason })).unwrap();
      toast.success('Purchase order rejected');
      setShowRejectModal(false);
      setRejectReason('');
    } catch (err: any) {
      toast.error(err || 'Failed to reject');
    }
  };

  const handleSubmit = async () => {
    if (!current || !current.can_submit) return;
    try {
      await dispatch(submitPurchaseOrder(current.id)).unwrap();
      toast.success('Submitted for approval');
    } catch (err: any) {
      toast.error(err || 'Failed to submit');
    }
  };

  const handleUnsubmit = async () => {
    if (!current || !current.can_unsubmit) return;
    try {
      await dispatch(unsubmitPurchaseOrder(current.id)).unwrap();
      toast.success('Unsubmitted');
    } catch (err: any) {
      toast.error(err || 'Failed to unsubmit');
    }
  };

  const handleCancel = async () => {
    if (!current || !current.can_cancel) return;
    if (!window.confirm('Cancel this purchase order?')) return;
    try {
      await dispatch(cancelPurchaseOrder({ id: current.id })).unwrap();
      toast.success('Purchase order cancelled');
    } catch (err: any) {
      toast.error(err || 'Failed to cancel');
    }
  };

  const handleDelete = async () => {
    if (!current || !current.can_delete) return;
    if (!window.confirm('Delete this purchase order? This cannot be undone.')) return;
    try {
      await dispatch(deletePurchaseOrder(current.id)).unwrap();
      toast.success('Purchase order deleted');
      navigate('/purchase-orders');
    } catch (err: any) {
      toast.error(err || 'Failed to delete');
    }
  };

  if (detailLoading) {
    return <div className="text-center p-10 dark:text-gray-300">Loading purchase order details...</div>;
  }

  if (!current) {
    return (
      <div className="text-center p-10 dark:text-gray-300">
        Purchase order not found or ID is missing.
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          Go Back
        </button>
      </div>
    );
  }


  const statusId = current.status_id;
  const submitted = typeof current.submitted === 'number' ? current.submitted !== 0 : current.submitted;
  let statusText = 'Pending';
  let statusColor = 'bg-yellow-400 text-yellow-900';
  if (statusId === 2) { statusText = 'Approved'; statusColor = 'bg-green-500 text-white'; }
  else if (statusId === 3) { statusText = 'Rejected'; statusColor = 'bg-red-500 text-white'; }
  else if (statusId === 4) { statusText = 'Cancelled'; statusColor = 'bg-orange-500 text-white'; }
  else if (submitted) { statusText = 'Submitted'; statusColor = 'bg-blue-400 text-blue-900'; }
  else if (current.reference_number && !current.order_number) { statusText = 'TBA'; statusColor = 'bg-purple-400 text-purple-900'; }

  let borderAccent = 'border-gray-500';
  if (statusId === 2) borderAccent = 'border-green-500';
  else if (statusId === 3) borderAccent = 'border-red-500';
  else if (statusId === 4) borderAccent = 'border-orange-500';
  else if (submitted) borderAccent = 'border-blue-500';
  else if (current.reference_number && !current.order_number) borderAccent = 'border-purple-500';

  return (
    <div className="container mx-auto p-4 md:p-6 lg:p-8 max-w-5xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        &larr; Back to List
      </button>

      {error && (
        <div className="my-4 p-3 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded-md shadow text-center">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden">
        {/* Header */}
        <div className={`p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 border-l-4 ${borderAccent}`}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                {current.order_number ? `PO #${current.order_number}` : `PO #${current.id}`}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Date: {formatDate(current.date)}
                {!!current.reference_number && ` | Ref: ${current.reference_number}`}
              </p>
            </div>
            <div className="flex items-center gap-2 mt-2 sm:mt-0 flex-wrap">
              {!!current.is_linked && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                  Linked Order
                </span>
              )}
              {!!(current.internal === 1) && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300">
                  Internal
                </span>
              )}
              {!!current.can_submit && (
                <button
                  onClick={handleSubmit}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-indigo-600 hover:bg-indigo-700 text-white transition-colors disabled:opacity-50"
                >
                  Submit
                </button>
              )}
              {!!current.can_unsubmit && (
                <button
                  onClick={handleUnsubmit}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 transition-colors disabled:opacity-50"
                >
                  Unsubmit
                </button>
              )}
              {!!current.can_cancel && (
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel Order
                </button>
              )}
              {!!current.can_delete && (
                <button
                  onClick={handleDelete}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-sm font-semibold rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                >
                  Delete
                </button>
              )}
              <span className={`px-3 py-1 text-sm font-semibold rounded-full ${statusColor}`}>
                {statusText}
              </span>
            </div>
          </div>
        </div>

        {/* Approve/Reject action bar */}
        {(!!current.can_approve || !!current.can_reject) && (
          <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex flex-col sm:flex-row gap-3">
            {!!current.can_approve && (
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className={`w-full sm:w-auto text-white font-bold py-2 px-4 rounded transition-colors duration-150 ease-in-out flex items-center justify-center bg-green-500 hover:bg-green-600 shadow-sm ${actionLoading ? 'opacity-70 cursor-wait' : ''}`}
              >
                {actionLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Approving...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                    Approve
                  </>
                )}
              </button>
            )}
            {!!current.can_reject && (
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
                className={`w-full sm:w-auto text-white font-bold py-2 px-4 rounded transition-colors duration-150 ease-in-out flex items-center justify-center bg-red-500 hover:bg-red-600 shadow-sm ${actionLoading ? 'opacity-70 cursor-wait' : ''}`}
              >
                {actionLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                    </svg>
                    Reject
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Main content */}
        <div className="p-4 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 gap-x-6 gap-y-4 md:gap-y-6">
            {/* Order Information — left column */}
            <div className="md:col-span-1 lg:col-span-1 space-y-3">
              <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-2 dark:border-gray-600">Order Information</h4>
              <div className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-sm">
                <ul className="space-y-2">
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Date:</span> {formatDate(current.date)}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Requested by:</span> {current.user?.name || 'N/A'}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      Store{(current.stores?.length || 0) > 1 ? `s (${current.stores!.length})` : ''}:
                    </span>{' '}
                    {(current.stores?.length || 0) <= STORE_PREVIEW_LIMIT ? (
                      <>{current.stores?.map((s) => s.name).join(', ') || 'N/A'}</>
                    ) : (
                      <span title={current.stores!.map((s) => s.name).join(', ')}>
                        {current.stores!.slice(0, STORE_PREVIEW_LIMIT).map((s) => s.name).join(', ')}
                        {' '}+{current.stores!.length - STORE_PREVIEW_LIMIT} more
                      </span>
                    )}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {current.internal === 1 ? 'Technician:' : 'Supplier:'}
                    </span>{' '}
                    {current.internal === 1 ? (current.technician?.name || 'N/A') : (current.supplier?.name || 'N/A')}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Fulfillment:</span> {current.fulfillment?.name || 'N/A'}
                  </li>
                  {!!current.category && (
                    <li className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Category:</span> {current.category.name}
                    </li>
                  )}
                  {!!current.subCategory && (
                    <li className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Sub-category:</span> {current.subCategory.name}
                    </li>
                  )}
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Internal:</span> {current.internal == 1 ? 'Yes' : 'No'}
                  </li>
                </ul>
              </div>
            </div>

            {/* Approval Chain — right column */}
            <div className="md:col-span-2 lg:col-span-1 space-y-3">
              <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-2 dark:border-gray-600">Approval Chain</h4>
              {current.approvers && current.approvers.length > 0 ? (
                <ul className="space-y-2">
                  {current.approvers.map((approver, index) => {
                    let approverStatus = 'Pending';
                    let bgColorClass = 'bg-gray-600 text-white dark:bg-gray-600 dark:text-white';
                    if (approver.status_id === 2) { approverStatus = 'Approved'; bgColorClass = 'bg-green-600 text-white dark:bg-green-600 dark:text-white'; }
                    else if (approver.status_id === 3) { approverStatus = 'Rejected'; bgColorClass = 'bg-red-600 text-white dark:bg-red-600 dark:text-white'; }
                    const borderColor = approver.status_id === 1 ? 'border-blue-200 dark:border-blue-700'
                      : approver.status_id === 2 ? 'border-green-200 dark:border-green-700'
                      : approver.status_id === 3 ? 'border-red-200 dark:border-red-700'
                      : 'border-gray-200 dark:border-gray-600';
                    return (
                      <li key={approver.id || index} className={`p-2.5 border rounded-md shadow-sm transition-colors ${bgColorClass} ${borderColor}`}>
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-medium text-sm text-white">{approver.user?.name || 'Unknown'}</span>
                            {!!approver.tba && (
                              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-white bg-opacity-10 text-white">TBA</span>
                            )}
                            {approver.status_id === 3 && approver.comment && (
                              <p className="text-xs text-red-200 dark:text-red-100 mt-0.5">Reason: {approver.comment}</p>
                            )}
                          </div>
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-white bg-opacity-10 text-white">
                            {approverStatus}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No approvers assigned.</p>
              )}
            </div>

            {/* Full-width sections */}
            <div className="md:col-span-3 lg:col-span-2 space-y-4">
              {/* Linked Orders */}
              {current.is_linked_order && current.linked_family && current.linked_family.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Linked Orders</h4>
                  <div className="space-y-2">
                    {current.linked_family.map((linked) => (
                      <Link
                        key={linked.id}
                        to={`/purchase-orders/${linked.id}`}
                        className="block p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-gray-800 dark:text-gray-200">{linked.order_number || ''}</span>
                            {linked.id === current.id && (
                              <span className="ml-2 text-xs text-blue-500">(this order)</span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {linked.stores?.map((s) => s.name).join(', ')}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* PO Items - matching requisitions details page format */}
              {((current.equipment_items && current.equipment_items.length > 0) || (current.assets && current.assets.length > 0) || (current.equipmentRequests && current.equipmentRequests.length > 0) || (current.items && current.items.length > 0)) && (
                <div>
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Items to Repair / Request</h4>
                  <div className="space-y-3">
                    {(() => {
                      const items: JSX.Element[] = [];

                      // Equipment items from Laravel (matches Livewire exactly)
                      if (current.equipment_items && current.equipment_items.length > 0) {
                        current.equipment_items.forEach((item: any, index: number) => {
                          items.push(
                            <div
                              key={`processed-eq-${index}`}
                              className="p-2.5 border border-amber-200 dark:border-amber-700 rounded-md bg-amber-100 dark:bg-amber-900/70 shadow-sm"
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  <strong>{item.description}</strong>
                                  {(item.category || item.subcategory) && (
                                    <><br />
                                      <small>Category: {item.category} {item.subcategory ? `Subcategory: ${item.subcategory}` : ''}</small>
                                    </>
                                  )}
                                </p>
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                  Equipment
                                </span>
                              </div>
                              <div className="mt-1 border-t border-amber-100 dark:border-amber-800 pt-1">
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  <span className="font-medium">Serial Number:</span> {item.serial_number || 'N/A'}
                                </p>
                                {item.repair_notes && (
                                  <div className="mt-1">
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Repair Notes:</span>
                                    </p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-line">{item.repair_notes}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                      }

                      // Asset description pairs
                      if (current.assets && current.assets.length > 0) {
                        current.assets.forEach((asset: any, index: number) => {
                          items.push(
                            <div
                              key={`processed-asset-${index}`}
                              className="p-2.5 border border-indigo-200 dark:border-indigo-700 rounded-md bg-indigo-100 dark:bg-indigo-900/70 shadow-sm"
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  Asset: {asset.asset}
                                </p>
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
                                  Asset
                                </span>
                              </div>
                              <div className="mt-1 border-t border-indigo-100 dark:border-indigo-800 pt-1">
                                {asset.descriptions && asset.descriptions.length > 0 ? (
                                  <div>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Description:</span>
                                    </p>
                                    <ul className="list-disc list-inside ml-2 text-xs text-gray-600 dark:text-gray-400">
                                      {asset.descriptions.map((desc: any, dIndex: number) => {
                                        const descText = typeof desc === 'string' ? desc : desc.description || '';
                                        return descText && <li key={dIndex}>{descText}</li>;
                                      })}
                                    </ul>
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">Description:</span> <em>No description</em>
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        });
                      }

                      // Fallback to legacy equipment requests
                      if (items.length === 0 && current.equipmentRequests && current.equipmentRequests.length > 0) {
                        current.equipmentRequests.forEach((item: any, index: number) => {
                          items.push(
                            <div
                              key={`eq-${item.id || index}`}
                              className="p-2.5 border border-amber-200 dark:border-amber-700 rounded-md bg-amber-100 dark:bg-amber-900/70 shadow-sm"
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  {item.equipment?.description || 'Equipment Item'}
                                  {item.equipment?.serial_number && (
                                    <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                                      (S/N: {item.equipment.serial_number})
                                    </span>
                                  )}
                                </p>
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                  Equipment
                                </span>
                              </div>
                            </div>
                          );
                        });
                      }

                      // Fallback to simple items table if no structured data
                      if (items.length === 0 && current.items && current.items.length > 0) {
                        return (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-400 dark:text-gray-500 border-b border-gray-200 dark:border-gray-600">
                                  <th className="py-2 pr-4">Description</th>
                                  <th className="py-2 pr-4">Qty</th>
                                  <th className="py-2 pr-4">Unit</th>
                                  <th className="py-2 pr-4">Price</th>
                                  <th className="py-2">VAT</th>
                                </tr>
                              </thead>
                              <tbody>
                                {current.items.map((item) => (
                                  <tr key={item.id} className="border-b border-gray-100 dark:border-gray-700">
                                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{item.description}</td>
                                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{item.quantity}</td>
                                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{item.unit_of_measure || 'EA'}</td>
                                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{formatCurrency(item.price)}</td>
                                    <td className="py-2 text-gray-700 dark:text-gray-300">{item.non_vat ? 'Exempt' : (item.vat || 'Inclusive')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      }

                      return items.length > 0 ? items : null;
                    })()}
                  </div>
                </div>
              )}

              {/* Quotation */}
              {current.quotation ? (
                <div className="mt-6">
                  <div className="p-3 border border-green-200 dark:border-green-700 rounded-md bg-green-50 dark:bg-green-900/70 shadow-sm">
                    <button
                      type="button"
                      className="flex items-center w-full justify-between text-left text-green-700 dark:text-green-200 font-semibold focus:outline-none"
                      onClick={() => setIsQuotationOpen((prev) => !prev)}
                    >
                      <div className="flex flex-wrap gap-x-6 gap-y-1 items-center w-full">
                        {current.quotation.quotation_number && (
                          <span className="text-sm font-medium">Quotation #: <span className="font-semibold">{current.quotation.quotation_number}</span></span>
                        )}
                        {current.quotation.quotation_date && (
                          <span className="text-sm font-medium">Date: <span className="font-normal">{formatDate(current.quotation.quotation_date)}</span></span>
                        )}
                        <span className="text-sm font-medium">Amount: <span className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(current.quotation.amount)}</span></span>
                        {current.quotation.vat !== undefined && (
                          <span className="text-sm font-medium">VAT: <span className="font-normal">{current.quotation.vat === 1 ? 'Inclusive' : 'Exclusive'}</span></span>
                        )}
                      </div>
                      <svg
                        className={`w-5 h-5 ml-2 transition-transform ${isQuotationOpen ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    {isQuotationOpen && (
                      <div className="mt-3 space-y-3">
                        {current.quotation.attachment && (
                          <div className="flex justify-end">
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Quotes/${current.quotation.attachment}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              View Quote
                            </a>
                          </div>
                        )}
                        {current.quotation.attachment && (
                          <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                            <iframe
                              src={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Quotes/${current.quotation.attachment}`}
                              className="w-full h-96"
                              title="Quote Document"
                              onError={(e) => {
                                (e.target as HTMLIFrameElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Invoices */}
              {current.invoices && current.invoices.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Invoices</h4>
                  <div className="space-y-2">
                    {current.invoices.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="text-gray-700 dark:text-gray-300">
                          <span className="font-medium">{inv.invoice_number || `Invoice #${inv.id}`}</span>
                          <span className="ml-3 text-gray-500 dark:text-gray-400">{formatCurrency(inv.amount)}</span>
                          {inv.date && (
                            <span className="ml-3 text-xs text-gray-400 dark:text-gray-500">{formatDate(inv.date)}</span>
                          )}
                        </div>
                        {inv.attachment && (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL || ''}/assets/Invoices/${inv.attachment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline text-sm"
                          >
                            View
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* GRNs */}
              {current.grns && current.grns.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">GRNs</h4>
                  <div className="space-y-2">
                    {current.grns.map((grn) => (
                      <div key={grn.id} className="flex items-center justify-between p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="text-gray-700 dark:text-gray-300">
                          <span className="font-medium">{grn.grn_number || `GRN #${grn.id}`}</span>
                          {grn.status && (
                            <span className="ml-3 text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              {grn.status}
                            </span>
                          )}
                        </div>
                        {grn.created_at && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">{formatDateTime(grn.created_at)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity History */}
              <div className="mt-6">
                <button
                  type="button"
                  className="flex items-center text-gray-700 dark:text-gray-300 font-semibold focus:outline-none"
                  onClick={() => setIsHistoryVisible(!isHistoryVisible)}
                >
                  <svg
                    className={`w-5 h-5 mr-2 transition-transform ${isHistoryVisible ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  History
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({isHistoryVisible ? 'Hide' : 'Show'})
                  </span>
                </button>
                {isHistoryVisible && current.histories && current.histories.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {[...current.histories].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((hist) => (
                      <div key={hist.id} className="flex items-start gap-3 p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-700 dark:text-gray-300">{hist.user?.name || 'Unknown'}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">{hist.action}</span>
                          </div>
                          {hist.comment && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{hist.comment}</p>
                          )}
                          <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 block">
                            {formatDateTime(hist.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : isHistoryVisible && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">No history entries for this purchase order.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
          <div className="bg-white dark:bg-gray-800 p-5 md:p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Reject Purchase Order #{current.order_number || current.id}</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason (required)"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-red-500 focus:border-red-500 h-28"
              rows={4}
            />
            <div className="mt-5 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || actionLoading}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md disabled:opacity-50 transition-colors"
              >
                {actionLoading ? 'Submitting...' : 'Submit Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderDetailPage;
